/**
 * PORTED — RO-11-04 / #44589.
 * Origin: `excellent/app/_chrome/dock-chat-transcript.tsx`.
 * No next/*; rendered only when DockBar's copilot surfaces are enabled.
 */

import * as React from "react";
import {
  ToolCallStep,
  ChatMessageView,
  AssistantMarkdown,
  Icon,
  cn,
  formatUsd,
} from "@excellent/ui";
import { TodoList } from "@excellent/ui/blocks/copilot/todo-list";
import { PermissionCard, type LivePermission } from "@excellent/ui/blocks/copilot/permission-card";
import { type ChatMessage } from "@excellent/ui/api/contracts/chat";
import { type ChatStreamFrame } from "@excellent/ui/api/contracts/chat-stream";

/**
 * The DockBar's chat-transcript family — extracted from `dock-bar.tsx` to keep
 * that file under the size budget. The pieces that feed each other:
 *   · `deriveLive` folds the raw stream frames into a `LiveState` (DockBar reads
 *     this for its panel-gating + permissions/todos/usage),
 *   · `deriveTurn` folds the SAME frames into an ordered turn (steps + the
 *     trailing answer) — the richer view the live transcript renders,
 *   · `ChatTranscript` renders persisted + optimistic + live messages.
 *
 * The live turn is deliberately calm (was: a thinking disclosure + prominent
 * narration prose + an ever-growing stack of expanded ToolCallSteps — a wall):
 *   1. A `ChatMark` + `WorkingLine` appears the instant a turn is busy, so
 *      "Thinking…" shows on send (no dead gap before the first frame).
 *   2. The WorkingLine shows only the CURRENT activity, humanized ("Looking up
 *      tools", "Checking the Pulse") — narration is demoted into it, not
 *      rendered as prose. It flashes; it doesn't accumulate.
 *   3. When the answer starts / the turn settles, the plumbing COLLAPSES to a
 *      one-line `TurnReceipt` ("Worked for 12s · 8 steps ▸", expandable — the
 *      full step list is one click away) above a clean, prominent answer.
 * → docs/design/CHAT-ACTIVITY-COLLAPSE.md.
 */

// ─── live state shape (DockBar-facing — unchanged) ──────────────────────────────

export interface LiveState {
  text: string;
  thinking: string;
  todos: Array<{ content: string; status: string }> | null;
  tools: LiveTool[];
  permissions: LivePermission[];
  usage: Extract<ChatStreamFrame, { type: "usage" }> | null;
  /** ACP mid-conversation agent switch toast (PRD D6). */
  agentSwitch: Extract<ChatStreamFrame, { type: "agent_switch" }> | null;
}
export interface LiveTool {
  id: string;
  name: string;
  summary?: string;
  input?: unknown;
  status: "allowed" | "denied" | "ran" | "failed";
  reason?: string;
}

// ─── humanize a tool name into a present-tense activity verb ─────────────────────
// The user reads "Checking the Pulse", never `mcp__excellent__pulse_bottlenecks`.

const TOOL_VERB: Record<string, string> = {
  ToolSearch: "Looking up tools",
  Read: "Reading a file",
  Grep: "Searching the code",
  Glob: "Searching the code",
  Edit: "Editing a file",
  Write: "Writing a file",
  Bash: "Running a command",
  WebSearch: "Searching the web",
  WebFetch: "Reading a page",
  db_query: "Querying your data",
  db_execute: "Updating your data",
  pulse_bottlenecks: "Checking the Pulse",
  dashboard_summary: "Reading the dashboard",
  workspace_status: "Reading the workspace",
  task_list: "Reading issues",
};

function humanizeTool(name: string): string {
  const base = name.replace(/^mcp__[a-z0-9]+__/i, "");
  const verb = TOOL_VERB[base];
  if (verb) return verb;
  const words = base.replace(/_/g, " ").trim();
  return words ? `Running ${words}` : "Working";
}

function fmtElapsed(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

// ─── the turn view (steps + trailing answer) ─────────────────────────────────────
// Same frames as `deriveLive`, segmented: a text block "seals" into a narration
// step the moment a tool follows it (Claude's real shape: "I'll do X" → tools →
// "Here's the answer"); the trailing block after the last tool is the answer.

type TurnToolStatus = "running" | "ran" | "failed";
interface TurnToolStep { kind: "tool"; id: string; name: string; status: TurnToolStatus; summary?: string }
interface TurnThoughtStep { kind: "thought"; text: string }
interface TurnThinkStep { kind: "thinking"; text: string }
type TurnStep = TurnToolStep | TurnThoughtStep | TurnThinkStep;

interface TurnView {
  steps: TurnStep[];
  current: { kind: "thinking" | "tool"; label: string } | null;
  answer: string;
}

export function deriveTurn(frames: ChatStreamFrame[]): TurnView {
  const steps: TurnStep[] = [];
  const tools = new Map<string, TurnToolStep>();
  let curText = "";
  let sawDelta = false;
  let curThink = "";

  const sealText = () => {
    if (curText.trim()) steps.push({ kind: "thought", text: curText.trim() });
    curText = "";
  };
  const sealThink = () => {
    if (curThink.trim()) steps.push({ kind: "thinking", text: curThink.trim() });
    curThink = "";
  };

  for (const f of frames) {
    if (f.type === "text_delta") {
      if (curThink.trim()) sealThink();
      curText += f.text;
      sawDelta = true;
    } else if (f.type === "text") {
      if (!sawDelta) {
        if (curThink.trim()) sealThink();
        curText += f.text;
      }
    } else if (f.type === "thinking") {
      if (curText.trim()) sealText();
      curThink += f.text;
    } else if (f.type === "tool_use") {
      if (f.name === "TodoWrite") continue;
      sealThink();
      sealText();
      const ts: TurnToolStep = {
        kind: "tool",
        id: f.id,
        name: f.name,
        status: f.verdict === "denied" ? "failed" : "running",
        summary: f.verdict === "denied" ? f.reason ?? undefined : undefined,
      };
      tools.set(f.id, ts);
      steps.push(ts);
    } else if (f.type === "tool_result") {
      const ts = tools.get(f.id);
      if (ts) {
        ts.status = f.ok ? "ran" : "failed";
        if (f.summary) ts.summary = f.summary;
      }
    }
    // todo / permission_* / usage / done / error → handled via deriveLive
  }

  let current: TurnView["current"] = null;
  const running = [...tools.values()].find((t) => t.status === "running");
  if (running) current = { kind: "tool", label: humanizeTool(running.name) };
  else if (curThink.trim()) current = { kind: "thinking", label: "Thinking…" };

  return { steps, current, answer: curText };
}

// ─── identity mark + working line + receipt ──────────────────────────────────────

/** The assistant's identity mark — a brand-tinted presence node (NOT a generic
 *  AI sparkle). Self-contained so it can anchor both the live + settled turn. */
export function ChatMark({ busy, size = "md" }: { busy?: boolean; size?: "sm" | "md" }) {
  const box = size === "sm" ? "size-4" : "size-6";
  return (
    <span className={cn("grid shrink-0 place-items-center rounded-full bg-primary/10", box)}>
      <span className={cn("rounded-full bg-primary", size === "sm" ? "size-1.5" : "size-2.5", busy && "animate-pulse")} />
    </span>
  );
}

function Spinner({ className }: { className?: string }) {
  return <Icon name="loader" className={cn("size-3.5 shrink-0 animate-spin text-muted-foreground", className)} />;
}

/** One calm line — spinner + cross-fading verb + meta — centered against the
 *  mark (`min-h-6`). The verb cross-fades via the `key` as the step changes. */
function WorkingLine({ turn, elapsedMs }: { turn: TurnView; elapsedMs: number }) {
  // t0: with no current activity yet (no frames / an inter-tool gap) fall back to
  // a generic label so the line is NEVER empty while busy.
  const label = turn.current?.label ?? (turn.steps.length > 0 ? "Working…" : "Thinking…");
  const n = turn.steps.length;
  return (
    <div className="flex min-h-6 items-center gap-2 type-body">
      <Spinner />
      <span key={label} className="animate-in fade-in-0 slide-in-from-bottom-1 text-foreground duration-200">
        {label}
      </span>
      <span className="type-subtext tabular-nums text-muted-foreground/50">
        {n > 0 ? `· ${n} step${n === 1 ? "" : "s"} ` : ""}· {fmtElapsed(elapsedMs)}
      </span>
    </div>
  );
}

/** The settled receipt — replaces the always-expanded tool stack. Collapsed by
 *  default; folds the demoted narration + thinking + tools behind one line. */
function TurnReceipt({ steps, elapsedMs }: { steps: TurnStep[]; elapsedMs: number }) {
  const [open, setOpen] = React.useState(false);
  if (steps.length === 0) return null;
  const failed = steps.some((s) => s.kind === "tool" && s.status === "failed");
  return (
    <div className="type-subtext">
      {/* button-ignore: quiet text disclosure toggle (mirrors ToolCallGroup), not an emphasis-graded action */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex min-h-6 items-center gap-1.5 text-muted-foreground transition-colors hover:text-foreground"
      >
        <Icon name={open ? "chevron-down" : "chevron-right"} size="xs" />
        <span>
          Worked for {fmtElapsed(elapsedMs)} · {steps.length} step{steps.length === 1 ? "" : "s"}
        </span>
        {failed && <span className="size-1.5 rounded-full bg-destructive" aria-label="a step failed" />}
      </button>
      {open && (
        <div className="mt-1.5 space-y-0.5 border-l-2 border-border/40 pl-2.5">
          {steps.map((s, i) =>
            s.kind === "tool" ? (
              <ToolCallStep
                key={`${s.id}-${i}`}
                name={s.name}
                summary={s.summary}
                status={s.status === "running" ? "allowed" : s.status}
              />
            ) : (
              <div key={`${s.kind}-${i}`} className="flex items-start gap-2 py-1.5 type-subtext text-muted-foreground">
                <Icon
                  name={s.kind === "thinking" ? "brain" : "message-square"}
                  className="mt-0.5 size-3 shrink-0 text-muted-foreground/50"
                />
                <span className="min-w-0 flex-1">{s.text}</span>
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
}

// ─── chat transcript (persisted + optimistic + live, mirrors CopilotPanel) ───────

export function ChatTranscript({
  messages,
  optimistic,
  live,
  frames,
  showLive,
  busy,
  error,
  onResolvePermission,
}: {
  messages: ChatMessage[];
  optimistic: string | null;
  live: LiveState;
  frames: ChatStreamFrame[];
  showLive: boolean;
  busy: boolean;
  error: string | null;
  onResolvePermission: (id: string, decision: "allow" | "deny", always: boolean) => void;
}) {
  const endRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length, optimistic, frames.length, busy, showLive]);

  // Turn clock — starts when a live turn begins and ticks while it's on screen
  // (showLive spans busy + the brief settled window before the message persists),
  // so the receipt can read "Worked for 12s" without DockBar tracking timing.
  const [, setTick] = React.useState(0);
  const startRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (!showLive) {
      startRef.current = null;
      return;
    }
    if (startRef.current == null) startRef.current = Date.now();
    const id = setInterval(() => setTick((t) => t + 1), 500);
    return () => clearInterval(id);
  }, [showLive]);
  const elapsedMs = startRef.current ? Date.now() - startRef.current : 0;

  const turn = React.useMemo(() => deriveTurn(frames), [frames]);
  const answering = turn.answer.trim().length > 0;

  return (
    <div className="max-h-[60vh] space-y-3 overflow-y-auto p-3">
      {messages.map((m) =>
        m.role === "assistant" ? (
          <div key={m.uid} className="flex items-start gap-2.5">
            <ChatMark />
            <div className="min-w-0 flex-1">
              <ChatMessageView message={m} />
            </div>
          </div>
        ) : (
          <ChatMessageView key={m.uid} message={m} />
        ),
      )}

      {optimistic !== null && (
        <div className="flex justify-end">
          <div className="max-w-[88%] rounded-md bg-secondary/40 px-3 py-2 type-body whitespace-pre-wrap text-foreground">
            {optimistic}
          </div>
        </div>
      )}

      {showLive && (
        <div className="flex flex-col gap-1.5">
          {/* ACP D6 mid-conversation agent switch toast */}
          {live.agentSwitch && (
            <p
              className="rounded-md border border-border/60 bg-muted/40 px-2.5 py-1.5 type-subtext text-muted-foreground"
              role="status"
            >
              {live.agentSwitch.message}
            </p>
          )}
          <div className="flex items-start gap-2.5">
            <ChatMark busy={busy} />
            <div className="min-w-0 flex-1">
              {busy && !answering ? (
                <WorkingLine turn={turn} elapsedMs={elapsedMs} />
              ) : busy && answering ? (
                // answer started → the plumbing has collapsed to a thin receipt line
                <div className="flex min-h-6 items-center gap-1.5 type-subtext text-muted-foreground">
                  <Spinner className="size-3" />
                  <span>
                    Worked for {fmtElapsed(elapsedMs)} · {turn.steps.length} step
                    {turn.steps.length === 1 ? "" : "s"}
                  </span>
                </div>
              ) : (
                <TurnReceipt steps={turn.steps} elapsedMs={elapsedMs} />
              )}

              {live.todos && (
                <div className="mt-1.5">
                  <TodoList items={live.todos} />
                </div>
              )}

              {answering && (
                <div className="mt-1.5 type-body text-foreground">
                  <AssistantMarkdown content={turn.answer} />
                  {busy && <span className="inline-block animate-pulse text-muted-foreground">▋</span>}
                </div>
              )}
            </div>
          </div>

          {live.permissions.map((p) => (
            <PermissionCard
              key={p.id}
              request={p}
              onResolve={(decision, always) => onResolvePermission(p.id, decision, always)}
            />
          ))}
        </div>
      )}

      {live.usage && (
        <p className="type-subtext text-muted-foreground/70">
          {live.usage.inputTokens ?? "–"} in / {live.usage.outputTokens ?? "–"} out tokens
          {live.usage.costUsd != null ? ` · ${formatUsd(live.usage.costUsd)}` : ""}
        </p>
      )}
      {error && <p className="type-subtext text-destructive">{error}</p>}
      {busy && <p className="text-center type-subtext text-muted-foreground">Press Esc to stop</p>}
      <div ref={endRef} />
    </div>
  );
}

// ─── live-frame folding (DockBar-facing — unchanged) ─────────────────────────────

export function deriveLive(frames: ChatStreamFrame[], resolvedPerms: Set<string>): LiveState {
  let text = "";
  let sawDelta = false;
  let thinking = "";
  let todos: LiveState["todos"] = null;
  const tools: LiveTool[] = [];
  const perms = new Map<string, LivePermission>();
  let usage: LiveState["usage"] = null;
  let agentSwitch: LiveState["agentSwitch"] = null;
  for (const f of frames) {
    if (f.type === "text_delta") {
      text += f.text;
      sawDelta = true;
    } else if (f.type === "text") {
      if (!sawDelta) text += f.text;
    } else if (f.type === "thinking") {
      thinking += f.text;
    } else if (f.type === "todo") {
      todos = f.items;
    } else if (f.type === "tool_use") {
      if (f.name === "TodoWrite") continue;
      tools.push({
        id: f.id,
        name: f.name,
        input: f.input,
        status: f.verdict === "denied" ? "denied" : "allowed",
        reason: f.reason,
      });
    } else if (f.type === "tool_result") {
      const t = tools.find((x) => x.id === f.id);
      if (t) {
        if (f.summary) t.summary = f.summary;
        t.status = f.ok ? "ran" : "failed";
      }
    } else if (f.type === "permission_request") {
      perms.set(f.id, { id: f.id, tool: f.tool, input: f.input, risk: f.risk, reason: f.reason });
    } else if (f.type === "permission_resolved") {
      perms.delete(f.id);
    } else if (f.type === "usage") {
      usage = f;
    } else if (f.type === "agent_switch") {
      agentSwitch = f;
    }
  }
  const permissions = [...perms.values()].filter((p) => !resolvedPerms.has(p.id));
  return { text, thinking, todos, tools, permissions, usage, agentSwitch };
}
